'use client'

import { useCallback, useEffect } from 'react'
import { showSystemNotification } from '@/components/system-notifications'
import { useSignOut } from '@/hooks/use-sign-out'
import { useSession } from '@clerk/nextjs'
import { notifications } from '@mantine/notifications'
import { Button, Space, Stack, Text } from '@mantine/core'
import { usePathname } from 'next/navigation'
import { INACTIVITY_TIMEOUT_MS, WARNING_THRESHOLD_MS } from '@/lib/types'
import { plural } from '@/lib/string'

interface InactivityWarningMessageProps {
    remainingMinutes: number
    onStaySignedIn: () => Promise<void>
}

const InactivityWarningMessage = ({ remainingMinutes, onStaySignedIn }: InactivityWarningMessageProps) => {
    return (
        <Stack>
            <Text>
                {`To keep your account secure, you'll be logged out in ${plural(remainingMinutes, 'minute')} due to inactivity. Click 'Stay Signed In' to continue your session.`}
            </Text>
            <Space h="xs" />
            <Button variant="filled" size="sm" onClick={onStaySignedIn}>
                Stay Signed In
            </Button>
        </Stack>
    )
}

export const ActivityContext = () => {
    const { session } = useSession()
    const signOut = useSignOut()
    const pathname = usePathname()

    const checkInactivity = useCallback(() => {
        if (!session?.lastActiveAt) {
            return
        }

        const lastActiveTime = new Date(session.lastActiveAt).getTime()
        const currentTime = Date.now()
        const inactivityDuration = currentTime - lastActiveTime

        if (inactivityDuration < WARNING_THRESHOLD_MS) {
            notifications.hide('session-expired')
        }

        if (inactivityDuration >= INACTIVITY_TIMEOUT_MS) {
            showSystemNotification({
                title: 'Session Expired',
                id: 'session-expired',
                message: "You've been logged out due to inactivity. Log back in to resume your work.",
            })
            notifications.hide('inactivity-warning')
            signOut()
        } else if (INACTIVITY_TIMEOUT_MS - inactivityDuration <= WARNING_THRESHOLD_MS) {
            const remainingMinutes = Math.ceil((INACTIVITY_TIMEOUT_MS - inactivityDuration) / 60000)

            const handleStaySignedIn = async () => {
                const updatedSession = await session.touch()
                if (updatedSession) {
                    notifications.hide('inactivity-warning')
                }
            }

            showSystemNotification({
                title: 'Session Expiration Warning',
                id: 'inactivity-warning',
                withCloseButton: false,
                message: (
                    <InactivityWarningMessage remainingMinutes={remainingMinutes} onStaySignedIn={handleStaySignedIn} />
                ),
            })
        }
    }, [session, signOut])

    useEffect(() => {
        if (!session) {
            notifications.hide('inactivity-warning')
            return
        }

        const intervalId = setInterval(checkInactivity, 10000)
        return () => clearInterval(intervalId)
    }, [session, checkInactivity])

    useEffect(() => {
        checkInactivity()
    }, [pathname, checkInactivity])

    return null
}
